import React, { useMemo, useState } from "react";
import {
  Box,
  Chip,
  Collapse,
  Link,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  alpha,
} from "@mui/material";
import UserAvatar from "bundles/Internal/components/common/UserAvatar";

// Shared expanded-row content listing a set of job applications. Used by both
// the Candidates collapse (variant "candidate" → shows the offer/company the
// candidate applied to) and the Job Offers collapse (variant "job_offer" →
// shows the candidate who applied). Each row surfaces WHO created the
// application: the internal user (via `created_by`) or, when the candidate
// self-applied through the platform, a "Form" label with the UTM source.
//
// When `showTalentFilter` is set, an All / Talent-team toggle (with counts) is
// rendered at the top of the collapse and filters the rows to applications sent
// from the talent team (sent_from_talent_team === true).

const APP_STATUS_COLOR = {
  pending: { bg: "rgba(245,124,0,0.10)", text: "#F57C00" },
  accepted: { bg: "rgba(7,147,68,0.10)", text: "#079344" },
  denied: { bg: "rgba(211,47,47,0.10)", text: "#D32F2F" },
  refused: { bg: "rgba(211,47,47,0.10)", text: "#D32F2F" },
};

const NEUTRAL = { bg: "rgba(42,51,78,0.08)", text: "#2A334E" };

const subHeadSx = {
  fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
  fontSize: 11,
  textTransform: "uppercase",
  letterSpacing: "0.5px",
  color: "#2A334E",
  borderBottom: "1px solid rgba(42,51,78,0.1)",
  py: 1,
};

const subCellSx = {
  fontSize: 12,
  fontFamily: "Segoe UI Regular, sans-serif, system-ui",
  color: "#2A334E",
  py: 0.75,
  borderBottom: "1px solid rgba(42,51,78,0.06)",
};

const statusPill = (status) => {
  if (!status) return "—";
  const c = APP_STATUS_COLOR[status] || NEUTRAL;
  return (
    <Chip
      label={String(status).replaceAll("_", " ")}
      size="small"
      sx={{
        backgroundColor: c.bg,
        color: c.text,
        fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
        fontSize: 10,
        borderRadius: "4px",
        height: 20,
      }}
    />
  );
};

const formatDate = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return new Intl.DateTimeFormat("fr-BE", {
    year: "2-digit",
    month: "2-digit",
    day: "2-digit",
  }).format(date);
};

const renderCreatedBy = (app) => {
  const creator = app?.created_by;
  if (creator) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
        <UserAvatar user={creator} size={20} />
        <Box component="span">{creator.full_name || creator.email || "Unknown"}</Box>
      </Box>
    );
  }
  // No internal user → the candidate applied through the platform form.
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
      <Chip
        label="Form"
        size="small"
        sx={{
          backgroundColor: "rgba(7,147,68,0.10)",
          color: "#079344",
          fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
          fontSize: 10,
          borderRadius: "4px",
          height: 20,
        }}
      />
      {app?.utm_source && (
        <Box component="span" sx={{ fontSize: 11, color: "rgba(42,51,78,0.5)" }}>
          {app.utm_source}
        </Box>
      )}
    </Box>
  );
};

const candidateName = (candidate) => {
  if (!candidate) return "—";
  return [candidate.first_name, candidate.last_name].filter(Boolean).join(" ") || candidate.email || "—";
};

const miniChip = (label, bg, text) => (
  <Chip
    label={label}
    size="small"
    sx={{
      backgroundColor: bg,
      color: text,
      fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
      fontSize: 10,
      borderRadius: "4px",
      height: 20,
    }}
  />
);

const renderStatus = (app) => (
  <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25, alignItems: "flex-start" }}>
    {statusPill(app.status)}
    {app.denial_reasons && (
      <Box component="span" sx={{ fontSize: 10, color: "#D32F2F" }}>{app.denial_reasons}</Box>
    )}
  </Box>
);

// How the application entered the platform — talent team, CV library, or a
// direct/self application (with its UTM source when available).
const renderSource = (app) => {
  const badges = [];
  if (app.sent_from_talent_team) badges.push(miniChip("Talent team", "rgba(7,147,68,0.10)", "#079344"));
  if (app.added_from_cvtheque) badges.push(miniChip("CV library", "rgba(25,118,210,0.10)", "#1976D2"));
  if (badges.length === 0) {
    return app.utm_source ? (
      <Box component="span" sx={{ fontSize: 11, color: "rgba(42,51,78,0.6)" }}>{app.utm_source}</Box>
    ) : (
      <Box component="span" sx={{ color: "rgba(42,51,78,0.4)" }}>Direct</Box>
    );
  }
  return (
    <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
      {badges.map((b, i) => <span key={i}>{b}</span>)}
    </Box>
  );
};

const renderResume = (app) => {
  if (!app.resume_url) return "—";
  return (
    <Link
      href={app.resume_url}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(e) => e.stopPropagation()}
      sx={{
        color: "#079344",
        fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
        textDecoration: "none",
        "&:hover": { textDecoration: "underline" },
      }}
    >
      Resume ↗
    </Link>
  );
};

// Matches the internal filter-chip look (see ExternalProfilesTableFilters).
const toggleChipSx = (active) => ({
  fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
  fontSize: 12,
  borderRadius: "10px",
  height: 30,
  cursor: "pointer",
  transition: "all 0.15s ease",
  ...(active
    ? {
      backgroundColor: alpha("#079344", 0.12),
      color: "#079344",
      borderColor: "#079344",
      "&:hover": { backgroundColor: alpha("#079344", 0.18) },
    }
    : {
      backgroundColor: "rgba(255,255,255,0.9)",
      color: "#2A334E",
      borderColor: "rgba(42,51,78,0.2)",
      "&:hover": { backgroundColor: "#fff", borderColor: "rgba(42,51,78,0.35)" },
    }),
});

const JobApplicationsCollapse = ({
  applications = [],
  variant = "candidate",
  emptyLabel = "No job applications.",
  showTalentFilter = false,
}) => {
  const [talentFilter, setTalentFilter] = useState("all");

  const talentCount = useMemo(
    () => (applications || []).filter((app) => app.sent_from_talent_team).length,
    [applications],
  );

  const visible = showTalentFilter && talentFilter === "talent"
    ? (applications || []).filter((app) => app.sent_from_talent_team)
    : (applications || []);

  const leadHeaders =
    variant === "job_offer" ? ["Candidate", "Contact"] : ["Job Offer", "Company"];
  const headers = [...leadHeaders, "Status", "Applied", "Source", "Created by", "Resume"];

  const toggle = showTalentFilter ? (
    <Box sx={{ display: "flex", gap: 1, mb: 1 }}>
      <Chip
        variant="outlined"
        label={`All (${(applications || []).length})`}
        onClick={(e) => { e.stopPropagation(); setTalentFilter("all"); }}
        sx={toggleChipSx(talentFilter === "all")}
      />
      <Chip
        variant="outlined"
        label={`Talent team (${talentCount})`}
        onClick={(e) => { e.stopPropagation(); setTalentFilter("talent"); }}
        sx={toggleChipSx(talentFilter === "talent")}
      />
    </Box>
  ) : null;

  return (
    <Collapse in timeout="auto" unmountOnExit>
      <Box sx={{ px: 2, py: 1.5, backgroundColor: "rgba(249,250,255,0.7)" }}>
        {toggle}
        {visible.length === 0 ? (
          <Box sx={{ fontSize: 13, color: "rgba(42,51,78,0.5)" }}>
            {emptyLabel}
          </Box>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                {headers.map((h) => (
                  <TableCell key={h} sx={subHeadSx}>
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {visible.map((app) => (
                <TableRow key={app.id} sx={{ "&:last-child td": { borderBottom: 0 } }}>
                  {variant === "job_offer" ? (
                    <>
                      <TableCell sx={subCellSx}>{candidateName(app.candidate)}</TableCell>
                      <TableCell sx={subCellSx}>
                        {app.candidate?.email || app.candidate?.phone || "—"}
                      </TableCell>
                    </>
                  ) : (
                    <>
                      <TableCell sx={subCellSx}>{app.job_title || "Spontaneous"}</TableCell>
                      <TableCell sx={subCellSx}>{app.company_name || "—"}</TableCell>
                    </>
                  )}
                  <TableCell sx={subCellSx}>{renderStatus(app)}</TableCell>
                  <TableCell sx={subCellSx}>{formatDate(app.created_at)}</TableCell>
                  <TableCell sx={subCellSx}>{renderSource(app)}</TableCell>
                  <TableCell sx={subCellSx}>{renderCreatedBy(app)}</TableCell>
                  <TableCell sx={subCellSx}>{renderResume(app)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Box>
    </Collapse>
  );
};

export default JobApplicationsCollapse;
